import { useEffect, useState } from 'react';
import { ArrowLeft, Lock, ShieldCheck } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { LegalFooter } from './LegalFooter';
import { loadCartId } from '../lib/cartStorage';
import { formatApiPrice, getCart, startCheckout, type CartPayload } from '../lib/commerceApi';
import { trackEvent } from '../lib/analytics';

export function CheckoutPage() {
  const [cart, setCart] = useState<CartPayload | null>(null);
  const [message, setMessage] = useState('Checkoutgegevens laden...');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Checkout | ProPickleball.nl';
    const cartId = loadCartId();
    if (!cartId) {
      setMessage('Er staat geen winkelwagen klaar.');
      return;
    }
    getCart(cartId)
      .then((payload) => {
        setCart(payload);
        setMessage(payload.items.every((item) => item.checkoutEligible) ? 'Vul je gegevens in om veilig naar Mollie te gaan.' : 'Niet alle producten zijn beschikbaar voor checkout.');
      })
      .catch(() => setMessage('De checkoutservice is tijdelijk niet bereikbaar.'));
  }, []);

  const canCheckout = Boolean(cart?.items.length && cart.items.every((item) => item.checkoutEligible));

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!cart || !canCheckout) {
      setMessage('Checkout is nog niet beschikbaar voor deze selectie.');
      trackEvent({ event: 'checkout_blocked', location: 'checkout', label: 'cart_not_eligible', routeFamily: 'checkout' });
      return;
    }
    const data = new FormData(event.currentTarget);
    const privacyAccepted = data.get('privacyAccepted') === 'on';
    if (!privacyAccepted) {
      setMessage('Accepteer de privacy- en servicevoorwaarden om verder te gaan.');
      trackEvent({ event: 'checkout_blocked', location: 'checkout', label: 'privacy_missing', routeFamily: 'checkout' });
      return;
    }
    setIsSubmitting(true);
    trackEvent({ event: 'begin_checkout', location: 'checkout', label: 'mollie_checkout_start', routeFamily: 'checkout' });
    try {
      const session = await startCheckout({
        cartId: cart.id,
        customer: {
          email: String(data.get('email') ?? '').trim(),
          name: String(data.get('name') ?? '').trim() || undefined,
          phone: String(data.get('phone') ?? '').trim() || undefined
        },
        privacyAccepted: true
      });
      if (session.checkoutUrl) {
        window.location.href = session.checkoutUrl;
        return;
      }
      setMessage('De betaalpagina kon niet worden geopend. Neem contact op met support.');
    } catch {
      setMessage('Betalen is nog niet beschikbaar. Je bestelling is niet geplaatst.');
      trackEvent({ event: 'checkout_blocked', location: 'checkout', label: 'payment_not_available', routeFamily: 'checkout' });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <main className="checkoutPage">
        <nav className="productTopbar" aria-label="Checkout navigatie">
          <BrandLogo compact />
          <a className="backHome" href="/winkelwagen/"><ArrowLeft size={18} /> Terug naar winkelwagen</a>
        </nav>

        <section className="checkoutShell">
          <div className="cartHeading">
            <p className="eyebrow">Veilig afrekenen</p>
            <h1>Checkout via Mollie.</h1>
            <p>De betaalstap loopt server-side via de VPS. Er worden geen betaal- of API-sleutels in de browser geplaatst.</p>
          </div>

          <div className="cartGrid">
            <form className="checkoutForm" onSubmit={handleSubmit}>
              <label>
                Naam
                <input name="name" placeholder="Voor- en achternaam" required disabled={!canCheckout || isSubmitting} />
              </label>
              <label>
                E-mailadres
                <input name="email" type="email" placeholder="naam@example.com" required disabled={!canCheckout || isSubmitting} />
              </label>
              <label>
                Telefoon
                <input name="phone" type="tel" placeholder="+31..." disabled={!canCheckout || isSubmitting} />
              </label>
              <label className="checkboxLine">
                <input name="privacyAccepted" type="checkbox" required disabled={!canCheckout || isSubmitting} />
                <span>
                  Ik ga akkoord met de <a href="/algemene-voorwaarden/" target="_blank" rel="noreferrer">algemene voorwaarden</a>,
                  {' '}<a href="/privacybeleid/" target="_blank" rel="noreferrer">privacyverklaring</a> en
                  {' '}<a href="/retouren/" target="_blank" rel="noreferrer">retourinformatie</a>.
                </span>
              </label>
              <button type="submit" disabled={!canCheckout || isSubmitting}>{isSubmitting ? 'Betaalpagina openen...' : 'Ga veilig naar Mollie'}</button>
              <p className="formNote" role="status">{message}</p>
            </form>

            <aside className="checkoutSummaryPanel">
              <h2>Je bestelling</h2>
              <dl>
                <div><dt>Producten</dt><dd>{cart?.items.length ?? 0}</dd></div>
                <div><dt>Subtotaal</dt><dd>{formatApiPrice(cart?.totalCents ?? 0)}</dd></div>
                <div><dt>Checkoutstatus</dt><dd>{canCheckout ? 'Vrijgegeven' : 'Geblokkeerd'}</dd></div>
              </dl>
              <div className="checkoutTrustList">
                <span><ShieldCheck size={16} /> Betalen via Mollie</span>
                <span><Lock size={16} /> Server-side ordercontrole</span>
                <span><ShieldCheck size={16} /> Geen fake voorraadclaims</span>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <LegalFooter />
    </>
  );
}
